import { Field, SectionCard } from '@atlas/ui-components'
import { Button } from '@atlas/ui-primitives'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ConfirmDialog } from '@/components/confirm/ConfirmDialog'
import { MergeValueAction } from '@/components/merge/MergeValueAction'
import type { ProductPage } from '@/domain/explore.types'
import { useUpdateProduct } from '@/hooks/useCatalogWrites'
import { MarketNames } from './MarketNames'
import { MetaJsonEditor } from './MetaJsonEditor'
import { TaxonomyMetaEditor } from './TaxonomyMetaEditor'

/**
 * Editing the product ROW, and the two irreversible things that can be done to it.
 *
 * `code` is absent for the same reason it is on the brand: it is the axis value's code too,
 * so changing it would rename a value every classification points at. That is a merge, and
 * the merge control is here.
 */

const RETIRED = 'retired'

/** The product's own fields, flattened for editing. */
interface ProductForm {
	name: string
	subtitle: string
	principalClaim: string
}

const formOf = (page: ProductPage): ProductForm => ({
	name: page.product.name,
	subtitle: page.product.subtitle ?? '',
	principalClaim: page.product.principalClaim ?? '',
})

/** Hiding and merging: the two things that take the product out of the catalogue. */
function DangerZone({ page }: { page: ProductPage }) {
	const update = useUpdateProduct(page.product.productId, page.market?.code ?? null)
	const navigate = useNavigate()
	const [hiding, setHiding] = useState(false)
	const isRetired = page.product.status === RETIRED

	return (
		<>
			<SectionCard
				title={isRetired ? 'Hidden' : 'Remove from the catalogue'}
				description="Neither deletes anything: a product's classifications outlive its listing."
			>
				<div className="flex flex-wrap items-center gap-3">
					{isRetired ? (
						<>
							<p className="flex-1 text-ds-text-muted text-sm">
								This product is hidden. It is not listed under its brand, and its existing deliverables stay classified
								to it.
							</p>
							<Button
								type="button"
								variant="secondary"
								className="rounded-ds-full"
								isDisabled={update.isPending}
								onPress={() => update.mutate({ status: 'active' })}
							>
								Restore
							</Button>
						</>
					) : (
						<>
							<p className="flex-1 text-ds-text-muted text-sm">
								Hiding stops the product being listed. Use it for a product that is no longer sold.
							</p>
							<Button
								type="button"
								variant="danger"
								className="rounded-ds-full"
								isDisabled={update.isPending}
								onPress={() => setHiding(true)}
							>
								Hide product
							</Button>
						</>
					)}
				</div>
				<div className="mt-4 flex flex-wrap items-center gap-3 border-ds-border border-t pt-4">
					<p className="flex-1 text-ds-text-muted text-sm">
						If this product is a duplicate of another, merge it: everything filed under it moves, and this one is
						retired.
					</p>
					<MergeValueAction
						valueId={page.product.valueId}
						code={page.product.code}
						label={page.product.name}
						onMerged={() => void navigate('/explore')}
					/>
				</div>
			</SectionCard>

			<ConfirmDialog
				open={hiding}
				title={`Hide “${page.product.name}”?`}
				description="It stops being listed under its brand. Nothing is deleted, and it can be restored here."
				confirmLabel="Hide product"
				confirmVariant="danger"
				busyLabel="Hiding…"
				submitting={update.isPending}
				onConfirm={() => update.mutate({ status: RETIRED }, { onSettled: () => setHiding(false) })}
				onCancel={() => setHiding(false)}
			/>
		</>
	)
}

export function ProductEditTab({ page }: { page: ProductPage }) {
	const update = useUpdateProduct(page.product.productId, page.market?.code ?? null)
	const [form, setForm] = useState<ProductForm>(() => formOf(page))
	const set = (part: Partial<ProductForm>) => setForm((current) => ({ ...current, ...part }))

	const save = () =>
		// Empty means "not recorded", which the column stores as NULL — an empty string would
		// render as a present-but-blank value everywhere the page reads it.
		update.mutate({
			name: form.name.trim(),
			subtitle: form.subtitle.trim() || null,
			principalClaim: form.principalClaim.trim() || null,
		})

	return (
		<div className="space-y-4">
			<SectionCard title="Product" description="What ATLAS records about the product itself.">
				<div className="grid gap-3 sm:grid-cols-2">
					<Field label="Name" value={form.name} onChange={(e) => set({ name: e.target.value })} />
					<Field label="Subtitle" value={form.subtitle} onChange={(e) => set({ subtitle: e.target.value })} />
					<Field
						label="Principal claim"
						value={form.principalClaim}
						onChange={(e) => set({ principalClaim: e.target.value })}
					/>
				</div>
				<div className="mt-4 flex justify-end gap-2">
					<Button
						type="button"
						variant="secondary"
						className="rounded-ds-full"
						isDisabled={update.isPending}
						onPress={() => setForm(formOf(page))}
					>
						Reset
					</Button>
					<Button
						type="button"
						className="rounded-ds-full px-5"
						isDisabled={update.isPending || !form.name.trim()}
						onPress={save}
					>
						{update.isPending ? 'Saving…' : 'Save'}
					</Button>
				</div>
			</SectionCard>

			<MarketNames
				valueId={page.product.valueId}
				subject="product"
				defaultName={page.product.name}
				mappings={page.namesByMarket}
			/>
			{/* TWO objects, not one. The catalog row's `meta` holds what the product page
			    writes; the axis value's holds what the taxonomy load wrote (`veeva_matched`,
			    `veeva_object_type`). The Meta tab reads both, so both must be editable —
			    otherwise a reader sees fields here that cannot be corrected. */}
			<MetaJsonEditor
				meta={page.product.meta}
				subject="product"
				title="Raw meta — catalog row"
				description="What the product row itself stores. Written by this page and by the seeders."
				saving={update.isPending}
				onSave={(meta) => update.mutate({ meta })}
			/>

			<TaxonomyMetaEditor valueId={page.product.valueId} meta={page.productValueMeta} subject="product" />

			<DangerZone page={page} />
		</div>
	)
}
